import React, { memo, useCallback } from "react";
import colors from "app/colors";
import { ReplyIcon } from "modules/icons";
import CircularIconButton from "../_shared/buttons/circular-icon-button";
import {
  FileGalleryFragment,
  MessageFragment,
  MessageThreadFragment,
} from "gql/graphql";
import {
  FILE_GALLERY_BUTTON_BOX_SIZE,
  FILE_GALLERY_BUTTON_BACKGROUND_COLOR,
  FILE_GALLERY_BUTTON_WIDTH,
} from "modules/utils/Constants";
import { VStack, Text } from "@chakra-ui/react";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import { useOverlay } from "modules/providers/overlay-provider";
import { isMessageThreadReadOnlyAndIsReader } from "modules/utils/Functions";
import { useAuth } from "modules/providers/auth-provider";
import useRemovedFromThread from "modules/hooks/use-removed-from-thread";
import { usePubSub } from "modules/providers/pub-sub-provider";

interface IProps {
  messageThread: MessageThreadFragment;
  message: MessageFragment;
  file: FileGalleryFragment;
}

const FileGalleryReplyButton = ({ messageThread, message, file }: IProps) => {
  const { me } = useAuth();
  const { closeModal, closeDrawer } = useOverlay();
  const { isMeRemoved } = useRemovedFromThread(messageThread);
  const { createDraft, setReplyQuotedMessage } = useMessageThreadDraft({
    draftId: message.messageThreadId,
  });
  const { publish } = usePubSub();

  const onReplyToFileClicked = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      await createDraft({
        type: messageThread.type,
        draftType: "reply",
      });
      setReplyQuotedMessage(message, file);
      publish("focus-quill-editor", message.messageThreadId);
      closeModal();
      closeDrawer();
    },
    [
      createDraft,
      messageThread.type,
      setReplyQuotedMessage,
      message,
      file,
      publish,
      closeModal,
      closeDrawer,
    ],
  );

  if (!me) return null;

  if (isMeRemoved || isMessageThreadReadOnlyAndIsReader(messageThread, me)) {
    return null;
  }

  return (
    <VStack spacing={1} w={FILE_GALLERY_BUTTON_WIDTH}>
      <CircularIconButton
        icon={
          <ReplyIcon
            fill={colors.white}
            boxSize={FILE_GALLERY_BUTTON_BOX_SIZE}
          />
        }
        onClick={onReplyToFileClicked}
        aria-label="reply to file"
        backgroundColor={FILE_GALLERY_BUTTON_BACKGROUND_COLOR}
        variant="hover"
      />
      <Text color={colors.grayPrimary} fontWeight={500} fontSize={10}>
        Reply
      </Text>
    </VStack>
  );
};

export default memo(FileGalleryReplyButton);
